import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import "./AdminLogin.scss";

import {
  FaUserShield,
  FaLock,
  FaUser,
  FaEye,
  FaEyeSlash,
  FaSpinner,
  FaShieldAlt,
} from "react-icons/fa";

const AdminLogin = () => {
  const [identifier, setIdentifier] = useState(""); // Username or Email
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  // Helper: Resolve username to email if a plain username was entered
  const resolveEmail = async (input) => {
    const cleanInput = input.trim();
    if (cleanInput.includes("@")) {
      return cleanInput; // Already an email
    }

    // Lookup username in the 'admins' collection
    const q = query(
      collection(db, "admins"),
      where("username", "==", cleanInput.toLowerCase()),
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      throw new Error("Admin username not recognized.");
    }

    const adminDoc = snap.docs[0].data();
    return adminDoc.email;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!identifier.trim() || !password) {
      setErrorMessage("Please enter both username/email and password.");
      return;
    }

    setIsLoading(true);

    try {
      // 1. Resolve email from username
      const targetEmail = await resolveEmail(identifier);

      // 2. Authenticate securely via Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        targetEmail,
        password,
      );
      const user = userCredential.user;

      // 3. Authorization Check: Verify admin status in Firestore
      const adminDocRef = doc(db, "admins", user.uid);
      const adminDocSnap = await getDoc(adminDocRef);

      // Also check fallback query if document ID isn't UID
      let isAuthorized = false;
      if (adminDocSnap.exists() && adminDocSnap.data().status === "Active") {
        isAuthorized = true;
      } else {
        const q = query(
          collection(db, "admins"),
          where("email", "==", user.email),
          where("status", "==", "Active"),
        );
        const qSnap = await getDocs(q);
        if (!qSnap.empty) isAuthorized = true;
      }

      if (!isAuthorized) {
        // Sign out unauthorized user immediately
        await signOut(auth);
        throw new Error("Access Denied: Account lacks Admin privileges.");
      }

      // 4. Secure Session established — navigate to dashboard
      navigate("/admin");
    } catch (err) {
      console.error("Admin authentication error:", err);
      if (
        err.code === "auth/invalid-credential" ||
        err.code === "auth/wrong-password" ||
        err.code === "auth/user-not-found"
      ) {
        setErrorMessage("Invalid username/email or password.");
      } else if (err.code === "auth/too-many-requests") {
        setErrorMessage("Too many failed attempts. Access locked temporarily.");
      } else {
        setErrorMessage(err.message || "Authentication failed. Try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="erp-admin-auth-wrapper">
      {/* Background ambient lighting */}
      <div className="ambient-glow glow-1" />
      <div className="ambient-glow glow-2" />

      <div className="admin-login-card erp-pop-in">
        {/* Brand Shield & Header */}
        <div className="card-header">
          <div className="shield-icon-badge">
            <FaUserShield className="main-shield" />
          </div>
          <h1>Terminal Access</h1>
          <p>Super Admin Gateway</p>
        </div>

        {/* Dynamic Error Feedback */}
        {errorMessage && (
          <div className="auth-error-alert">
            <FaShieldAlt />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} className="auth-form" noValidate>
          {/* Username / Email Field */}
          <div className="input-field-group">
            <label>Admin Identifier</label>
            <div className="input-wrapper">
              <FaUser className="field-icon" />
              <input
                type="text"
                placeholder="Username or Email"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                disabled={isLoading}
                required
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="input-field-group">
            <label>Master Security Key</label>
            <div className="input-wrapper">
              <FaLock className="field-icon" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="toggle-eye-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label="Toggle password view"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-admin-submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FaSpinner className="spin-icon" /> Authenticating Session...
              </>
            ) : (
              "Authenticate & Launch"
            )}
          </button>
        </form>

        {/* Security Footer */}
        <div className="card-footer">
          <span className="security-tag">
            <FaShieldAlt /> 256-Bit Encrypted Authentication Channel
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
